import { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../components/ConfirmModal';
import { FaFileContract, FaExternalLinkAlt, FaShieldAlt, FaCalendarAlt, FaClock, FaStickyNote, FaEdit, FaTrash, FaTimes, FaPlus } from 'react-icons/fa';
import TenantSidebar from './TenantSidebar';
import TenantNavbar from './TenantNavbar';
import LoadingScreen from '../PropertyManager/LoadingScreen';
import '../PropertyManager/Sidebar.css';
import '../PropertyManager/Navbar.css';
import './TenantDocuments.css';
import Tutorial from '../PropertyManager/Tutorial';

const BASE_URL = 'https://api.rside.app';

const TD_STEPS = [
  {
    title: 'Your Documents',
    description: 'Access your lease details and all important documents from this page.',
  },
  {
    title: 'Rent & Lease',
    description: 'Your monthly rent, lease type, deposit, and due date are all shown here.',
    anchorId: 'td-rent-card',
    cardAlignment: 'bottom',
  },
  {
    title: 'Lease Term',
    description: 'See your lease start and end dates, and how many days are remaining.',
    anchorId: 'td-lease-term',
    cardAlignment: 'bottom',
  },
  {
    title: 'Documents',
    description: 'Download your lease agreement and view other property documents here.',
    anchorId: 'td-doc-section',
    cardAlignment: 'top',
  },
];

function fmt$(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseLeaseDate(str) {
  if (!str) return null;
  const base = str.split('T')[0];
  const parts = base.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    // ISO: YYYY-MM-DD
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  }
  // Legacy: M-D-Y
  return new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
}

function fmtLeaseDate(str) {
  const date = parseLeaseDate(str);
  if (!date) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysRemaining(endStr) {
  const end = parseLeaseDate(endStr);
  if (!end) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function parseFees(str) {
  if (!str) return [];
  return str.split(', ').map(item => {
    const match = item.match(/^(.+) - (\d+\.?\d*)$/);
    return match ? { name: match[1].trim(), amount: parseFloat(match[2]) } : null;
  }).filter(Boolean);
}

function ordinal(n) {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
}

function fmtDateShort(str) {
  if (!str) return '';
  try { return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return str; }
}

function TenantDocuments() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('');
  const [initials, setInitials] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const askConfirm = (opts, onConfirm) => setConfirmDialog({ ...opts, onConfirm });
  const [leaseData, setLeaseData] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [doorId, setDoorId] = useState(null);

  // Stored docs state
  const [storedDocs, setStoredDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [docForm, setDocForm] = useState({ document_name: '', file: null });
  const [docSaving, setDocSaving] = useState(false);
  const [tenantId, setTenantId] = useState(null);
  const [pmId, setPmId] = useState(null);
  const [isDocEditMode, setIsDocEditMode] = useState(false);
  const [editingNames, setEditingNames] = useState({});

  const fetchStoredDocs = async (dId) => {
    if (!dId) return;
    setDocsLoading(true);
    try {
      const r = await fetch(`${BASE_URL}/GetStoredDocsByDoor?door_id=${dId}`);
      if (r.ok) {
        const d = await r.json();
        setStoredDocs((d.documents || []).filter(doc => doc.ShareWithTenant === 1));
      }
    } catch {}
    setDocsLoading(false);
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate('/tenant/login'); return; }
      try {
        const sessionRes = await fetch(`${BASE_URL}/TenantApp/SessionInfo?email=${encodeURIComponent(user.email)}`);
        const session = await sessionRes.json();
        const name = session?.tenant?.full_name || '';
        setTenantName(name);
        setInitials(name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2));
        setUserEmail(user.email);

        const dId = session?.tenant?.door_id;
        const tId = session?.tenant?.tenant_id || session?.tenant?.id;
        setDoorId(dId);
        setTenantId(tId);
        const pmEmail = session?.property_manager?.email;
        if (pmEmail) {
          try {
            const acctRes = await fetch(`${BASE_URL}/MyAccount?Email=${encodeURIComponent(pmEmail)}`);
            if (acctRes.ok) {
              const acct = await acctRes.json();
              setPmId(acct.id || acct.PM_ID || acct.pm_id || null);
            }
          } catch {}
        }
        if (dId) {
          const [leaseRes] = await Promise.all([
            fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${dId}`),
          ]);
          const data = await leaseRes.json();
          setLeaseData(data?.tenants?.[0] || null);
          await fetchStoredDocs(dId);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  const handleOpenAddDoc = () => {
    setEditingDocId(null);
    setDocForm({ document_name: '', file: null });
    setShowDocModal(true);
  };

  const handleOpenEditDoc = (doc) => {
    setEditingDocId(doc.id);
    setDocForm({ document_name: doc.Document_Name || '', file: null });
    setShowDocModal(true);
  };

  const handleDeleteDoc = (docId) => {
    askConfirm(
      { title: 'Delete Document', message: 'Delete this document?', confirmLabel: 'Delete', variant: 'danger' },
      async () => { try {
        await fetch(`${BASE_URL}/DeleteStoredDocs?document_id=${docId}`, { method: 'DELETE' });
        setStoredDocs(prev => prev.filter(d => d.id !== docId));
      } catch {} }
    );
  };

  const handleEnterDocEditMode = () => {
    const names = {};
    storedDocs.forEach(d => { names[d.id] = d.Document_Name || ''; });
    setEditingNames(names);
    setIsDocEditMode(true);
  };

  const handleExitDocEditMode = async () => {
    const changed = storedDocs.filter(d =>
      editingNames[d.id] !== undefined &&
      editingNames[d.id].trim() &&
      editingNames[d.id] !== (d.Document_Name || '')
    );
    for (const doc of changed) {
      try {
        const params = new URLSearchParams({ document_id: doc.id, document_name: editingNames[doc.id], share_with_tenant: true });
        await fetch(`${BASE_URL}/EditStoredDocs?${params}`, { method: 'PUT' });
        setStoredDocs(prev => prev.map(d => d.id === doc.id ? { ...d, Document_Name: editingNames[doc.id] } : d));
      } catch {}
    }
    setIsDocEditMode(false);
  };

  const handleSaveDoc = async () => {
    if (!docForm.document_name.trim()) return;
    setDocSaving(true);
    try {
      if (editingDocId) {
        const params = new URLSearchParams({
          document_id: editingDocId,
          document_name: docForm.document_name,
          share_with_tenant: true,
        });
        await fetch(`${BASE_URL}/EditStoredDocs?${params}`, { method: 'PUT' });
        setStoredDocs(prev => prev.map(d => d.id === editingDocId ? { ...d, Document_Name: docForm.document_name } : d));
      } else {
        if (!docForm.file) { setDocSaving(false); return; }
        const fd = new FormData();
        fd.append('file', docForm.file);
        const params = new URLSearchParams({
          door_id: doorId,
          document_name: docForm.document_name,
          share_with_tenant: true,
          owner: 'Tenant',
        });
        if (pmId) params.set('pm_id', pmId);
        await fetch(`${BASE_URL}/AddStoredDocs?${params}`, { method: 'POST', body: fd });
        await fetchStoredDocs(doorId);
      }
      setShowDocModal(false);
    } catch {}
    setDocSaving(false);
  };

  const openStoredDoc = async (docId) => {
    try {
      const res = await fetch(`${BASE_URL}/ViewStoredDocs?document_id=${docId}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {}
  };

  if (loading) return <LoadingScreen />;

  const lease = leaseData;
  const remaining = lease ? daysRemaining(lease.End_Date) : 0;
  const fees = parseFees(lease?.Fee_Details);
  const leaseType = lease?.MonthToMonth ? 'MONTH-TO-MONTH' : 'FIXED TERM';

  return (
    <div className="home-page">
      <TenantSidebar initials={initials} />
      <div className="main-content">
        <TenantNavbar title="Documents" tenantName={tenantName} initials={initials} />

        <div className="td-body">
          <div className="td-dashboard">

            {/* ── LEFT: Rent card + Documents ── */}
            <div className="td-dash-left">

              <div id="td-rent-card" className="td-rent-card">
                <div className="td-rent-top">
                  <span className="td-rent-label">MONTHLY RENT</span>
                  <span className="td-rent-type">{leaseType}</span>
                </div>
                <div className="td-rent-amount">{fmt$(lease?.Rent)}</div>
                <div className="td-rent-due">
                  <FaCalendarAlt style={{ fontSize: 12, opacity: 0.8 }} />
                  Due on the {ordinal(lease?.Rent_Due || 1)} of each month
                </div>
                <div className="td-rent-divider" />
                <div className="td-rent-deposit">
                  <FaShieldAlt style={{ fontSize: 12, opacity: 0.8 }} />
                  Deposit: {fmt$(lease?.Deposit)}
                </div>
              </div>

              {lease?.Lease_url && (
                <a href={lease.Lease_url} target="_blank" rel="noreferrer" className="td-doc-link">
                  <div className="td-doc-icon"><FaFileContract /></div>
                  <div className="td-doc-info">
                    <span className="td-doc-title">Lease Document</span>
                    <span className="td-doc-sub">View or download your lease agreement</span>
                  </div>
                  <FaExternalLinkAlt className="td-doc-ext" />
                </a>
              )}

              <div id="td-doc-section" className="td-section-label td-other-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span>OTHER DOCUMENTS</span>
                <div style={{display:'flex',gap:8}}>
                  {storedDocs.length > 0 && (
                    <button className="td-edit-docs-btn" onClick={isDocEditMode ? handleExitDocEditMode : handleEnterDocEditMode}>
                      {isDocEditMode ? 'Done' : <><FaEdit style={{fontSize:11}} /> Edit</>}
                    </button>
                  )}
                  {!isDocEditMode && <button className="td-add-doc-btn" onClick={handleOpenAddDoc}><FaPlus /> Upload</button>}
                </div>
              </div>

              {docsLoading ? (
                <div className="td-section-card td-docs-loading">Loading…</div>
              ) : storedDocs.length === 0 ? (
                <div className="td-section-card td-docs-empty">
                  <FaStickyNote style={{fontSize:26,color:'#d1d5db',marginBottom:8}} />
                  <p style={{margin:0,color:'#9ca3af',fontSize:14}}>No documents yet</p>
                  <button className="td-add-doc-btn" style={{marginTop:12}} onClick={handleOpenAddDoc}><FaPlus /> Upload Document</button>
                </div>
              ) : (
                <div className="td-section-card td-other-card">
                  {storedDocs.map((doc, i) => (
                    <div key={doc.id || i}>
                      {i > 0 && <div className="td-other-divider" />}
                      <div className="td-doc-entry">
                        <div className="td-doc-entry-hd">
                          <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                            <div className="td-doc-file-icon-t"><FaStickyNote /></div>
                            <div style={{minWidth:0,flex:1}}>
                              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                                {isDocEditMode && doc.Owner === 'Tenant' ? (
                                  <input
                                    className="td-doc-name-input"
                                    value={editingNames[doc.id] ?? doc.Document_Name ?? ''}
                                    onChange={e => setEditingNames(p => ({...p, [doc.id]: e.target.value}))}
                                  />
                                ) : (
                                  <button className="td-doc-name-link" onClick={() => openStoredDoc(doc.id)}>
                                    {doc.Document_Name || 'Document'}
                                  </button>
                                )}
                                {doc.Upload_Date && (
                                  <span style={{fontSize:12,color:'#9ca3af',flexShrink:0,whiteSpace:'nowrap'}}>Uploaded <span style={{color:'#374151',fontWeight:500}}>{fmtDateShort(doc.Upload_Date)}</span></span>
                                )}
                              </div>
                              {doc.Owner === 'Manager' && (
                                <div className="td-doc-meta">
                                  <span className="td-doc-owner-badge">Uploaded by Manager</span>
                                </div>
                              )}
                            </div>
                          </div>
                          {doc.Owner === 'Tenant' && isDocEditMode && (
                            <button className="td-doc-icon-btn td-doc-icon-btn-red" style={{flexShrink:0}} onClick={() => handleDeleteDoc(doc.id)}><FaTrash /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="td-doc-add-more-wrap">
                    <button className="td-doc-add-more-btn" onClick={handleOpenAddDoc}><FaPlus /> Upload Another</button>
                  </div>
                </div>
              )}

              {/* Doc Upload/Edit Modal */}
              {showDocModal && (
                <div className="td-doc-modal-overlay" onClick={() => setShowDocModal(false)}>
                  <div className="td-doc-modal-card" onClick={e => e.stopPropagation()}>
                    <div className="td-doc-modal-hd">
                      <h3 style={{margin:0,fontSize:17,fontWeight:700}}>{editingDocId ? 'Edit Document' : 'Upload Document'}</h3>
                      <button className="td-doc-modal-close" onClick={() => setShowDocModal(false)}><FaTimes /></button>
                    </div>
                    <div style={{padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        <label style={{fontSize:13,fontWeight:600,color:'#374151'}}>Document Name <span style={{color:'#dc2626'}}>*</span></label>
                        <input
                          className="td-doc-modal-input"
                          placeholder="e.g. Renters Insurance, Inspection Report"
                          value={docForm.document_name}
                          onChange={e => setDocForm(p => ({...p, document_name: e.target.value}))}
                        />
                      </div>
                      {!editingDocId && (
                        <div style={{display:'flex',flexDirection:'column',gap:6}}>
                          <label style={{fontSize:13,fontWeight:600,color:'#374151'}}>File <span style={{color:'#dc2626'}}>*</span></label>
                          <input
                            type="file"
                            className="td-doc-modal-input"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={e => setDocForm(p => ({...p, file: e.target.files[0] || null}))}
                          />
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex',gap:10,padding:'4px 20px 20px'}}>
                      <button className="td-doc-modal-cancel" onClick={() => setShowDocModal(false)}>Cancel</button>
                      <button
                        className="td-doc-modal-save"
                        onClick={handleSaveDoc}
                        disabled={docSaving || !docForm.document_name.trim() || (!editingDocId && !docForm.file)}
                      >
                        {docSaving ? 'Saving…' : editingDocId ? 'Save' : 'Upload'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* ── RIGHT: Lease term + Fees ── */}
            <div className="td-dash-right">

              <div id="td-lease-term" className="td-section-card">
                <span className="td-section-label">IMPORTANT DATES</span>
                <span className="td-section-label" style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'none', letterSpacing: 0, marginTop: -4 }}>Lease Period</span>
                <div className="td-lease-dates">
                  <div className="td-lease-side">
                    <span className="td-lease-side-lbl">START</span>
                    <span className="td-lease-side-val">{fmtLeaseDate(lease?.Start_Date)}</span>
                  </div>
                  <div className="td-lease-arrow">
                    <span className="td-lease-arrow-circle">→</span>
                  </div>
                  <div className="td-lease-side td-lease-end">
                    <span className="td-lease-side-lbl">END</span>
                    <span className="td-lease-side-val">{fmtLeaseDate(lease?.End_Date)}</span>
                  </div>
                </div>
                {remaining > 0 && (
                  <div className="td-days-pill">
                    <FaClock style={{ fontSize: 11 }} />
                    {remaining.toLocaleString()} days remaining
                  </div>
                )}
              </div>

              <div className="td-section-card">
                <span className="td-section-label">FEES &amp; CHARGES</span>
                {fees.length > 0 ? (
                  <div className="td-fees-list">
                    {fees.map((fee, i) => (
                      <div key={i} className="td-fee-row">
                        <span className="td-fee-dot" />
                        <span className="td-fee-name">{fee.name}</span>
                        <span className="td-fee-amt">{fmt$(fee.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{margin:0,fontSize:13,color:'#9ca3af'}}>No fees</p>
                )}
              </div>

            </div>

          </div>
        </div>
      </div>
      <Tutorial
        steps={TD_STEPS}
        storageKey={`hasSeenTenantDocsTutorial_${userEmail}`}
      />
      {confirmDialog && (
        <ConfirmModal
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          variant={confirmDialog.variant || 'danger'}
          onConfirm={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}
          onCancel={() => setConfirmDialog(null)}
        />
      )}
    </div>
  );
}

export default TenantDocuments;
